import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, useUser, SignIn } from '@clerk/react';
import { useTranslation } from '@yorukiri/i18n';
import { Button } from '@yorukiri/ui';
import { colors } from '@yorukiri/ui/tokens';
import { api } from '../lib/api';

export function InviteRoute() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [status, setStatus] = useState<'idle' | 'accepting' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !token || status !== 'idle') return;
    setStatus('accepting');
    api
      .post<{ pageId: string; role: string }>('/invites/accept', { token })
      .then((res) => navigate(`/pages/${res.pageId}`, { replace: true }))
      .catch((err) => {
        setStatus('error');
        setError((err as Error).message);
      });
  }, [isLoaded, isSignedIn, token, status, navigate]);

  if (!isLoaded) {
    return (
      <div style={shell}>
        <div style={{ color: colors.ink3, fontSize: 13 }}>{tCommon('loading')}</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div style={shell}>
        <SignIn routing="hash" signUpUrl="/sign-up" forceRedirectUrl={`/invite/${token}`} />
      </div>
    );
  }

  return (
    <div style={shell}>
      <div style={card}>
        <h1 style={{ fontWeight: 600, letterSpacing: '-0.015em', fontSize: 24, margin: 0, marginBottom: 8 }}>
          {t('invite_accept_title')}
        </h1>
        <p style={{ color: colors.ink3, fontSize: 13, marginBottom: 20 }}>
          {t('invite_accept_body', { email: user?.primaryEmailAddress?.emailAddress ?? '' })}
        </p>
        {error && <div style={{ color: colors.bad, fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
        {status === 'accepting' && <div style={{ color: colors.ink3 }}>{tCommon('loading')}</div>}
        {status === 'error' && (
          <Button onClick={() => setStatus('idle')}>{t('invite_accept_button')}</Button>
        )}
      </div>
    </div>
  );
}

const shell: React.CSSProperties = {
  minHeight: '100vh',
  background: colors.bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const card: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.line}`,
  borderRadius: 14,
  padding: 28,
  maxWidth: 400,
  width: '100%',
};
